import type { ChecklistItem, ChecklistSidebarProps } from '../types/components'
import { useState, useEffect, useRef } from 'react'
import { X, List, Loader2, ClipboardList } from 'lucide-react'
import {
  getChecklistTemplate,
  saveChecklist,
  addApplicationNote,
  addUnderwriterApplicantNote,
} from '../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { usePermission } from '@/hooks/usePermission'
import {
  MentionTextarea,
  applyTaggingToNotes,
} from '@/components/notes/MentionTextarea'
import styles from '../styles/applicationDetail.module.css'



export function ChecklistSidebar({
  showChecklist,
  onToggle,
  applicantId,
  applicationId,
  applicationData,
  onRefresh,
}: ChecklistSidebarProps) {
  const { hasPolicy } = usePermission()
  const [loading, setLoading] = useState(false)
  const [checklistData, setChecklistData] = useState<ChecklistItem[]>([])
  const [decision, setDecision] = useState<string>('')
  const hydratedApplicantIdRef = useRef<string | null>(null)
  const [uwNoteBody, setUwNoteBody] = useState('')
  const [csNoteBody, setCsNoteBody] = useState('')
  const [uwTaggingContent, setUwTaggingContent] = useState<
    Record<string, string>
  >({})
  const [csTaggingContent, setCsTaggingContent] = useState<
    Record<string, string>
  >({})
  const [uwSaving, setUwSaving] = useState(false)
  const [csSaving, setCsSaving] = useState(false)

  useEffect(() => {
    if (!applicationData) {
      return
    }

    const dataApplicantId = applicationData.applicant_id
    if (
      dataApplicantId != null &&
      String(dataApplicantId) !== String(applicantId)
    ) {
      return
    }

    if (hydratedApplicantIdRef.current === applicantId) {
      return
    }

    setChecklistData([])
    setDecision('')

    let cancelled = false

    const loadChecklist = async () => {
      setLoading(true)
      try {
        const template = await getChecklistTemplate()
        if (cancelled) {
          return
        }

        if (template?.checkbox_data) {
          const initializedData = template.checkbox_data.map(
            (item: ChecklistItem) => ({
              ...item,
              keyname: null,
              child: item.child
                ? item.child.map((childItem: ChecklistItem) => ({
                    ...childItem,
                    keyname: null,
                  }))
                : [],
            }),
          )
          setChecklistData(initializedData)

          const savedData = applicationData.applicant_checkbox_data as
            | Record<string, unknown>
            | undefined

          if (savedData) {
            const rawDecision = savedData.decision
            if (rawDecision != null && rawDecision !== '') {
              setDecision(String(rawDecision))
            } else {
              setDecision('')
            }

            const updatedData = initializedData.map((item: ChecklistItem) => {
              const savedItem = savedData[item.key] as
                | {
                    checked?: boolean
                    child?: Array<{ key: string; checked?: boolean }>
                  }
                | undefined
              if (savedItem) {
                return {
                  ...item,
                  keyname: savedItem.checked || false,
                  child: item.child
                    ? item.child.map((childItem: ChecklistItem) => {
                        const savedChild = savedItem.child?.find(
                          (c) => c.key === childItem.key,
                        )
                        return {
                          ...childItem,
                          keyname: savedChild?.checked || false,
                        }
                      })
                    : [],
                }
              }
              return item
            })
            setChecklistData(updatedData)
          }
        }

        hydratedApplicantIdRef.current = applicantId
      } catch (error: unknown) {
        if (!cancelled) {
          showError(
            error instanceof Error ? error.message : 'Failed to load checklist',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadChecklist()
    return () => {
      cancelled = true
    }
  }, [applicantId, applicationData])

  const saveChecklistData = async (
    data: ChecklistItem[],
    decisionValue: string,
    showNotification = false,
  ) => {
    try {
      const checkElements: Record<string, unknown> = {}

      data.forEach((item) => {
        if (!checkElements[item.key]) {
          checkElements[item.key] = {
            key: item.key,
            label: item.label,
            checked: item.keyname === true,
            child: [] as Array<Record<string, unknown>>,
          }
        }

        if (item.child && item.child.length > 0) {
          item.child.forEach((childItem) => {
            if (childItem.keyname === true) {
              const parent = checkElements[item.key] as {
                child: Array<Record<string, unknown>>
              }
              parent.child.push({
                key: childItem.key,
                label: childItem.label,
                checked: childItem.keyname === true,
              })
            }
          })
        }
      })

      checkElements['decision'] = decisionValue

      await saveChecklist(applicantId, {
        applicant_checkbox_data: checkElements,
      })
      if (showNotification) {
        showSuccess('Checklist saved successfully')
      }
    } catch (error: unknown) {
      if (showNotification) {
        showError(error instanceof Error ? error.message : 'Failed to save checklist')
      }
    }
  }

  /** Angular `updateChecklist()` on parent — toggles parent only; children are independent. */
  const handleParentCheckboxChange = (index: number, checked: boolean) => {
    const updatedData = [...checklistData]
    updatedData[index] = {
      ...updatedData[index],
      keyname: checked,
    }
    setChecklistData(updatedData)
    void saveChecklistData(updatedData, decision, false)
  }

  /**
   * Angular `updateChecklist(parentControl, childData)` — when every child is checked
   * and the parent is still unchecked, auto-check the parent. Unchecking a child does
   * not uncheck the parent.
   */
  const handleChildCheckboxChange = (
    parentIndex: number,
    childIndex: number,
    checked: boolean,
  ) => {
    const updatedData = [...checklistData]
    const children = updatedData[parentIndex].child
    if (!children?.length) {
      return
    }

    children[childIndex] = {
      ...children[childIndex],
      keyname: checked,
    }

    const allChildrenChecked = children.every((child) => Boolean(child.keyname))
    if (allChildrenChecked && !updatedData[parentIndex].keyname) {
      updatedData[parentIndex] = {
        ...updatedData[parentIndex],
        keyname: true,
      }
    }

    setChecklistData(updatedData)
    void saveChecklistData(updatedData, decision, false)
  }

  const resolvedApplicationId = applicationId ?? applicationData?.application_id

  const submitUwNote = async () => {
    if (!uwNoteBody.trim()) {
      showError('Please enter a note')
      return
    }
    setUwSaving(true)
    try {
      const noteBody = applyTaggingToNotes(uwNoteBody.trim(), uwTaggingContent)
      await addUnderwriterApplicantNote(applicantId, noteBody)
      showSuccess('Note Added Successfully!')
      setUwNoteBody('')
      setUwTaggingContent({})
      await onRefresh?.()
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Failed to add note')
    } finally {
      setUwSaving(false)
    }
  }

  const submitCsNote = async () => {
    if (!csNoteBody.trim()) {
      showError('Please enter a note')
      return
    }
    if (
      resolvedApplicationId == null ||
      resolvedApplicationId === '' ||
      (typeof resolvedApplicationId !== 'string' &&
        typeof resolvedApplicationId !== 'number')
    ) {
      showError('Application ID not available.')
      return
    }
    setCsSaving(true)
    try {
      const noteBody = applyTaggingToNotes(csNoteBody.trim(), csTaggingContent)
      await addApplicationNote(resolvedApplicationId, noteBody)
      showSuccess('Note Added Successfully!')
      setCsNoteBody('')
      setCsTaggingContent({})
      await onRefresh?.()
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Failed to add note')
    } finally {
      setCsSaving(false)
    }
  }

  const renderChecklistItem = (item: ChecklistItem, actualIndex: number) => (
    <div
      key={item.key}
      className={styles.checklistItem}
      data-testid={`application-detail-checklist-item-${item.key}`}
    >
      <label
        className={styles.checklistItemLabel}
        data-testid={`application-detail-checklist-item-${item.key}-label`}
      >
        <input
          type="checkbox"
          id={`checkbox_${actualIndex}`}
          data-testid={`application-detail-checklist-parent-checkbox-${item.key}`}
          checked={item.keyname === true}
          onChange={(e) =>
            handleParentCheckboxChange(actualIndex, e.target.checked)
          }
          className={styles.checklistCheckbox}
        />
        <span
          className={styles.checklistItemText}
          dangerouslySetInnerHTML={{ __html: item.label }}
        />
      </label>
      {item.child && item.child.length > 0 ? (
        <div
          className={styles.checklistChildren}
          data-testid={`application-detail-checklist-item-${item.key}-children`}
        >
          {item.child.map((childItem, childIndex) => (
            <label
              key={childItem.key}
              className={styles.checklistChildLabel}
              data-testid={`application-detail-checklist-child-${item.key}-${childItem.key}-label`}
            >
              <input
                type="checkbox"
                id={`checkbox_${actualIndex}_${childIndex}`}
                data-testid={`application-detail-checklist-child-checkbox-${item.key}-${childItem.key}`}
                checked={childItem.keyname === true}
                onChange={(e) =>
                  handleChildCheckboxChange(
                    actualIndex,
                    childIndex,
                    e.target.checked,
                  )
                }
                className={styles.checklistCheckbox}
              />
              <span
                className={styles.checklistChildText}
                dangerouslySetInnerHTML={{ __html: childItem.label }}
              />
            </label>
          ))}
        </div>
      ) : null}
    </div>
  )

  if (!showChecklist) {
    return (
      <button
        className={styles.checklistBtn}
        onClick={onToggle}
        title="checklist"
        type="button"
        aria-label="Open checklist"
        data-testid="application-detail-checklist-open-button"
      >
        <List className="h-5 w-5" aria-hidden />
      </button>
    )
  }

  return (
    <div
      id="sidebar"
      className={styles.checklistRight}
      data-testid="application-detail-checklist-sidebar"
    >
      <div className={styles.checklistHeader} data-testid="application-detail-checklist-header">
        <div className={styles.checklistHeaderMain}>
          <span className={styles.checklistHeaderIcon} aria-hidden>
            <ClipboardList className="h-5 w-5" />
          </span>
          <div>
            <h2
              className={styles.checklistTitle}
              data-testid="application-detail-checklist-title"
            >
              Screening Checklist
            </h2>
            <p className={styles.checklistSubtitle}>Review items and add notes</p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={styles.closeButton}
          type="button"
          aria-label="Close checklist"
          data-testid="application-detail-checklist-close-button"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <div className={styles.checklistContent} data-testid="application-detail-checklist-content">
        {loading ? (
          <div className={styles.loadingState} data-testid="application-detail-checklist-loading">
            <Loader2 className={styles.checklistLoadingSpinner} aria-hidden />
            <span>Loading checklist…</span>
          </div>
        ) : (
          <div className={styles.checklistForm} data-testid="application-detail-checklist-form">
            {checklistData.length > 0 ? (
              <section
                className={styles.checklistCard}
                data-testid="application-detail-checklist-items"
              >
                <p className={styles.checklistSectionTitle}>Checklist items</p>
                <div className={styles.checklistItemsContainer}>
                  {checklistData.map((item, actualIndex) =>
                    renderChecklistItem(item, actualIndex),
                  )}
                </div>
              </section>
            ) : null}

            {hasPolicy(['application_detail_add_underwriting_notes_view']) ? (
              <section
                className={styles.checklistCard}
                data-testid="application-detail-checklist-uw-notes-section"
              >
                <div className={styles.checklistSectionHeader}>
                  <label
                    htmlFor="uw-note-checklist"
                    className={styles.checklistSectionTitle}
                    data-testid="application-detail-checklist-uw-notes-label"
                  >
                    Underwriting Notes
                  </label>
                  <button
                    type="button"
                    className={styles.checklistSubmitBtn}
                    data-testid="application-detail-checklist-uw-notes-submit-button"
                    disabled={uwSaving || !uwNoteBody.trim()}
                    onClick={() => void submitUwNote()}
                  >
                    {uwSaving ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
                <MentionTextarea
                  textareaId="uw-note-checklist"
                  className={styles.checklistTextarea}
                  dataTestId="application-detail-checklist-uw-notes-textarea"
                  value={uwNoteBody}
                  onChange={setUwNoteBody}
                  disabled={uwSaving}
                  placeholder="Add Notes (use @ to mention Liberty users)"
                  rows={4}
                  onTaggingAdd={(displayKey, useridToken) => {
                    setUwTaggingContent((prev) => ({
                      ...prev,
                      [displayKey]: useridToken,
                    }))
                  }}
                />
              </section>
            ) : null}

            {hasPolicy(['application_detail_add_notes_edit']) ? (
              <section
                className={styles.checklistCard}
                data-testid="application-detail-checklist-cs-notes-section"
              >
                <div className={styles.checklistSectionHeader}>
                  <label
                    htmlFor="cs-note-checklist"
                    className={styles.checklistSectionTitle}
                    data-testid="application-detail-checklist-cs-notes-label"
                  >
                    Customer Service Notes
                  </label>
                  <button
                    type="button"
                    className={styles.checklistSubmitBtn}
                    data-testid="application-detail-checklist-cs-notes-submit-button"
                    disabled={csSaving || !csNoteBody.trim()}
                    onClick={() => void submitCsNote()}
                  >
                    {csSaving ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
                <MentionTextarea
                  textareaId="cs-note-checklist"
                  className={styles.checklistTextarea}
                  dataTestId="application-detail-checklist-cs-notes-textarea"
                  value={csNoteBody}
                  onChange={setCsNoteBody}
                  disabled={csSaving}
                  placeholder="Add Notes (use @ to mention Liberty users)"
                  rows={4}
                  onTaggingAdd={(displayKey, useridToken) => {
                    setCsTaggingContent((prev) => ({
                      ...prev,
                      [displayKey]: useridToken,
                    }))
                  }}
                />
              </section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
